/**
 * Load persisted weekly fuel prices (FuelPriceDeclared) for Control Centre Fuel.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './apiClient';
import { shiftYmd, todayYmdInZone } from '../lib/calc';
import {
  trimPriceHistoryPreview,
  type FuelPriceSource,
  type FuelPriceWeekEntry,
} from './fuelPriceHistory';
import { DECLARED_INGEST_TIMEOUT_MS } from './fuelTransactionUpload';

export type FuelPriceDeclaredRow = {
  week_commencing: string;
  pence_per_litre: number;
  entry_method: 'pdf_regex' | 'manual' | string;
  source_filename?: string;
  received_at?: string;
  event_id?: string;
};

export type FuelPricesResponse = {
  prices?: FuelPriceDeclaredRow[];
  error?: string;
  message?: string;
  _note?: string;
};

const LOOKBACK_DAYS = 26 * 7; // ~6 months of week-commencing dates
const LOOKAHEAD_DAYS = 14;

function toIsoRangeBound(ymd: string, endOfDay: boolean): string {
  return endOfDay ? `${ymd}T23:59:59.999Z` : `${ymd}T00:00:00.000Z`;
}

export function fuelPriceHistoryQueryRange(asOf: Date = new Date()): { from: string; to: string } {
  const today = todayYmdInZone('Europe/London', asOf);
  return {
    from: toIsoRangeBound(shiftYmd(today, -LOOKBACK_DAYS), false),
    to: toIsoRangeBound(shiftYmd(today, LOOKAHEAD_DAYS), true),
  };
}

export function declaredRowToHistoryEntry(row: FuelPriceDeclaredRow): FuelPriceWeekEntry | null {
  const weekOf = String(row.week_commencing ?? '').trim();
  const pencePerLitre = Number(row.pence_per_litre);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekOf) || !Number.isFinite(pencePerLitre) || pencePerLitre <= 0) {
    return null;
  }
  const source: FuelPriceSource = row.entry_method === 'pdf_regex' ? 'pdf' : 'manual';
  return {
    weekOf,
    pencePerLitre,
    source,
    filename: row.source_filename,
  };
}

/**
 * Append the forward-filled preview week (latest confirmed + 7 days, same rate)
 * and trim. `confirmed` must be sorted newest-first and non-empty.
 */
function withPreviewWeek(confirmed: FuelPriceWeekEntry[]): FuelPriceWeekEntry[] {
  return trimPriceHistoryPreview([
    ...confirmed,
    {
      weekOf: shiftYmd(confirmed[0].weekOf, 7),
      pencePerLitre: confirmed[0].pencePerLitre,
      source: 'inferred',
    },
  ]);
}

/** Confirmed weeks from server + one session preview week. */
export function historyFromDeclaredPrices(rows: FuelPriceDeclaredRow[]): FuelPriceWeekEntry[] {
  // The truth layer is append-only, so a corrected week can arrive as a second
  // row for the same week_commencing. The server SQL already keeps the latest
  // per week, but collapse defensively (first row wins — rows are latest-first)
  // so the chart never draws two points / duplicate React keys for one week.
  const byWeek = new Map<string, FuelPriceWeekEntry>();
  for (const row of rows) {
    const entry = declaredRowToHistoryEntry(row);
    if (entry && !byWeek.has(entry.weekOf)) byWeek.set(entry.weekOf, entry);
  }
  const confirmed = [...byWeek.values()];
  if (confirmed.length === 0) return [];
  confirmed.sort((a, b) => b.weekOf.localeCompare(a.weekOf));
  return withPreviewWeek(confirmed);
}

/**
 * Merge server history with optimistic local confirmed weeks not yet visible in Iceberg
 * (ingest is async after Save). For the same week, prefer local — the just-saved rate
 * can land before Iceberg reflects it, and overwriting with stale server data races.
 */
export function mergeFuelPriceHistories(
  server: FuelPriceWeekEntry[],
  local: FuelPriceWeekEntry[],
): FuelPriceWeekEntry[] {
  const byWeek = new Map<string, FuelPriceWeekEntry>();
  for (const e of server) {
    if (e.source === 'inferred') continue;
    byWeek.set(e.weekOf, e);
  }
  for (const e of local) {
    if (e.source === 'inferred') continue;
    byWeek.set(e.weekOf, e);
  }
  const confirmed = [...byWeek.values()].sort((a, b) => b.weekOf.localeCompare(a.weekOf));
  if (confirmed.length === 0) return trimPriceHistoryPreview(local);
  return withPreviewWeek(confirmed);
}

export async function fetchDeclaredFuelPrices(
  timeRange?: { from: string; to: string },
): Promise<{ response: Response; json: FuelPricesResponse; entries: FuelPriceWeekEntry[] }> {
  const range = timeRange ?? fuelPriceHistoryQueryRange();
  const response = await authenticatedFetch(
    API_ENDPOINTS.fleetIntelligence.fuelPrices(range),
    { method: 'GET' },
    DECLARED_INGEST_TIMEOUT_MS,
  );
  const json = (await response.json().catch(() => ({}))) as FuelPricesResponse;
  const entries = historyFromDeclaredPrices(json.prices ?? []);
  return { response, json, entries };
}
